import {useState} from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';


type FAQType = {
    id: number;
    question: string;
    answer: string;
    details: string[];
    videoUrl: string;
    category: string;
};

const FAQSection = () => {
    const [expandedFAQ, setExpandedFAQ] = useState<number | null>(0);
    const [playingVideo, setPlayingVideo] = useState<number | null>(null);

    const faqs: FAQType[] = [
        {
            id: 0,
            question: 'How long does implementation take for a typical assembly?',
            answer: `Complete implementation typically takes 4-6 weeks from contract signing to full deployment. This includes system setup, data migration, staff training, and citizen onboarding.`,
            details: [
                'Week 1-2: System configuration and integration with existing infrastructure',
                'Week 3-4: Staff training programs and pilot testing with select revenue streams',
                'Week 5-6: Full deployment, citizen education campaigns, and performance optimization'
            ],
            videoUrl: 'https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=400&h=300&fit=crop',
            category: 'Implementation'
        },
        {
            id: 1,
            question: 'What training is provided for assembly staff and revenue collectors?',
            answer: `Comprehensive training program includes both technical and operational aspects, delivered through multiple channels to ensure all staff are confident using the system.`,
            details: [
                'In-person training workshops for management and key staff members',
                'Mobile training units for field collectors with hands-on device practice',
                'Online training modules accessible 24/7 with progress tracking',
                'Ongoing support hotline and WhatsApp group for quick assistance'
            ],
            videoUrl: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=300&fit=crop',
            category: 'Training'
        },
        {
            id: 2,
            question: 'How does the system work in rural areas with poor internet connectivity?',
            answer: `Our offline-first architecture ensures revenue collection continues even without internet connection, with automatic synchronization when connectivity is restored.`,
            details: [
                'Offline data storage on collector devices for up to 1000 transactions',
                'Automatic sync when internet connection is available (WiFi, mobile data)',
                'SMS-based backup for critical transaction confirmations',
                'Solar-powered charging stations for remote area deployment'
            ],
            videoUrl: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400&h=300&fit=crop',
            category: 'Technical'
        },
        {
            id: 3,
            question: 'What are the costs involved and how is pricing structured?',
            answer: `Transparent pricing based on assembly size and transaction volume, with no hidden fees and flexible payment options to match government budget cycles.`,
            details: [
                'Setup fee: One-time cost covering installation, training, and initial support',
                'Monthly subscription: Based on assembly size (Metro/Municipal/District)',
                'Transaction fees: Small percentage per successful digital payment',
                'Government discount: Special rates for multi-assembly implementations'
            ],
            videoUrl: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=300&fit=crop',
            category: 'Pricing'
        },
        {
            id: 4,
            question: 'How secure is citizen data and financial information?',
            answer: `Bank-grade security with multiple layers of protection, full compliance with Ghana Data Protection Act, and regular security audits by independent firms.`,
            details: [
                'AES-256 encryption for all data storage and transmission',
                'Multi-factor authentication for all system access',
                'Regular penetration testing and security audits',
                'Compliance with Ghana Data Protection Act and international standards'
            ],
            videoUrl: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=400&h=300&fit=crop',
            category: 'Security'
        },
        {
            id: 5,
            question: 'Can the system integrate with our existing accounting software?',
            answer: `Yes, Civicrate integrates with most popular accounting systems used by assemblies, including custom solutions and government-mandated reporting systems.`,
            details: [
                'Direct integration with GIFMIS (Government Integrated Financial Management System)',
                'API connections to popular accounting software (QuickBooks, Sage, etc.)',
                'Custom integration development for legacy systems',
                'Automated report generation in required government formats'
            ],
            videoUrl: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop',
            category: 'Integration'
        }
    ];

    const categories = ['All', 'Implementation', 'Training', 'Technical', 'Pricing', 'Security', 'Integration'];
    const [activeCategory, setActiveCategory] = useState('All');

    const filteredFAQs = activeCategory === 'All'
        ? faqs
        : faqs?.filter(faq => faq?.category === activeCategory);

    const toggleFAQ = (id: number) => {
        setExpandedFAQ(expandedFAQ === id ? null : id);
    };

    const handleVideoPlay = (faqId: number) => {
        setPlayingVideo(faqId);
        // In a real implementation, this would trigger video playback
        setTimeout(() => setPlayingVideo(null), 3000); // Simulate video end
    };

    return (
        <section className="py-20 bg-white">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
                        Frequently Asked Questions
                    </h2>
                    <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                        Get answers to common questions about implementing Civicrate in your assembly.
                        Watch video explanations for detailed insights.
                    </p>
                </div>

                {/* Category Filter */}
                <div className="flex flex-wrap justify-center gap-2 mb-12">
                    {categories?.map((category) => (
                        <button
                            key={category}
                            onClick={() => setActiveCategory(category)}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                                activeCategory === category
                                    ? 'bg-primary text-white shadow-md'
                                    : 'bg-muted text-muted-foreground hover:bg-muted-foreground hover:text-white'
                            }`}
                        >
                            {category}
                        </button>
                    ))}
                </div>

                {/* FAQ Grid */}
                <div className="grid lg:grid-cols-2 gap-8">
                    {filteredFAQs?.map((faq) => (
                        <div
                            key={faq?.id}
                            className={`bg-muted/30 rounded-2xl overflow-hidden transition-all duration-300 ${
                                expandedFAQ === faq?.id ? 'shadow-xl' : 'shadow-md hover:shadow-lg'
                            }`}
                        >
                            {/* FAQ Header */}
                            <button
                                onClick={() => toggleFAQ(faq?.id)}
                                className="w-full p-6 text-left focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset"
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1 pr-4">
                                        <div className="flex items-center space-x-2 mb-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          faq?.category === 'Implementation' ? 'bg-primary/10 text-primary' :
                              faq?.category === 'Training' ? 'bg-success/10 text-success' :
                                  faq?.category === 'Technical' ? 'bg-accent/10 text-accent' :
                                      faq?.category === 'Pricing' ? 'bg-warning/10 text-warning' :
                                          faq?.category === 'Security' ? 'bg-error/10 text-error' : 'bg-secondary/10 text-secondary'
                      }`}>
                        {faq?.category}
                      </span>
                                        </div>
                                        <h3 className="text-lg font-semibold text-foreground mb-2">
                                            {faq?.question}
                                        </h3>
                                        <p className="text-muted-foreground text-sm">
                                            {faq?.answer}
                                        </p>
                                    </div>
                                    <div className="flex flex-col items-center space-y-2">
                                        <Icon
                                            name={expandedFAQ === faq?.id ? "ChevronUp" : "ChevronDown"}
                                            size={20}
                                            className="text-muted-foreground"
                                        />
                                        {/*<Button*/}
                                        {/*    variant="outline"*/}
                                        {/*    size="xs"*/}
                                        {/*    onClick={(e) => {*/}
                                        {/*        e?.stopPropagation();*/}
                                        {/*        handleVideoPlay(faq?.id);*/}
                                        {/*    }}*/}
                                        {/*    iconName="Play"*/}
                                        {/*    className="opacity-70 hover:opacity-100"*/}
                                        {/*>*/}
                                        {/*    Video*/}
                                        {/*</Button>*/}
                                    </div>
                                </div>
                            </button>

                            {/* Expanded Content */}
                            {expandedFAQ === faq?.id && (
                                <div className="px-6 pb-6 animate-fade-in">
                                    <div className="border-t border-border pt-6">
                                        {/* Video Section */}
                                        <div className="mb-6">
                                            <div className="relative rounded-lg overflow-hidden bg-slate-100">
                                                <img
                                                    src={faq?.videoUrl}
                                                    alt={`${faq?.question} explanation video`}
                                                    className="w-full h-48 object-cover"
                                                />
                                                <div
                                                    className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                                    <Button
                                                        variant="default"
                                                        onClick={() => handleVideoPlay(faq?.id)}
                                                        iconName="Play"
                                                        iconPosition="left"
                                                        className="bg-white text-primary hover:bg-white/90"
                                                    >
                                                        {playingVideo === faq?.id ? 'Playing...' : 'Watch Explanation'}
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Detailed Information */}
                                        <div className="space-y-4">
                                            <h4 className="font-semibold text-foreground">Detailed Breakdown:</h4>
                                            <div className="space-y-3">
                                                {faq?.details?.map((detail, index) => (
                                                    <div key={index} className="flex items-start space-x-3">
                                                        <div
                                                            className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                                            <span
                                                                className="text-xs font-bold text-primary">{index + 1}</span>
                                                        </div>
                                                        <p className="text-sm text-muted-foreground">{detail}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Additional Resources */}
                                        <div className="mt-6 p-4 bg-primary/5 rounded-lg">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h5 className="font-medium text-foreground">Need More
                                                        Information?</h5>
                                                    <p className="text-sm text-muted-foreground">
                                                        Schedule a call with our implementation specialist
                                                    </p>
                                                </div>
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    iconName="Phone"
                                                    iconPosition="left"
                                                >
                                                    Schedule Call
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Contact Support */}
                <div className="mt-16 bg-gradient-to-r from-primary/5 to-success/5 rounded-2xl p-8 text-center">
                    <div className="max-w-3xl mx-auto">
                        <h3 className="text-2xl font-bold text-foreground mb-4">
                            Still Have Questions?
                        </h3>
                        <p className="text-lg text-muted-foreground mb-8">
                            Our implementation specialists are available to answer any specific questions
                            about your assembly's unique requirements.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="text-center">
                                <div
                                    className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Icon name="Phone" size={24} color="var(--color-primary)"/>
                                </div>
                                <h4 className="font-semibold text-foreground mb-2">Phone Support</h4>
                                <p className="text-sm text-muted-foreground mb-3">
                                    Speak directly with our experts
                                </p>
                                <Button variant="outline" size="sm">
                                    +233 XX XXX XXXX
                                </Button>
                            </div>

                            <div className="text-center">
                                <div
                                    className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Icon name="MessageCircle" size={24} color="var(--color-success)"/>
                                </div>
                                <h4 className="font-semibold text-foreground mb-2">WhatsApp Chat</h4>
                                <p className="text-sm text-muted-foreground mb-3">
                                    Quick responses via WhatsApp
                                </p>
                                <Button variant="outline" size="sm">
                                    Start Chat
                                </Button>
                            </div>

                            <div className="text-center">
                                <div
                                    className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Icon name="Mail" size={24} color="var(--color-accent)"/>
                                </div>
                                <h4 className="font-semibold text-foreground mb-2">Email Support</h4>
                                <p className="text-sm text-muted-foreground mb-3">
                                    Detailed written responses
                                </p>
                                <Button variant="outline" size="sm">
                                    Send Email
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FAQSection;